import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'agrinexus_cookie_consent_v1';

export function getCookieConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
  catch { return null; }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const saved = getCookieConsent();
    if (!saved) setVisible(true);
  }, []);

  const save = (value) => {
    const payload = { ...value, savedAt: new Date().toISOString(), version: 1 };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: payload }));
    setVisible(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => save(prefs);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-title"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] pb-[env(safe-area-inset-bottom)] pointer-events-none"
    >
      <div className="mx-auto max-w-5xl m-3 sm:m-4 pointer-events-auto">
        <div className="rounded-2xl border border-border/60 bg-background/95 backdrop-blur shadow-[0_10px_40px_rgba(0,0,0,0.35)] p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="hidden sm:grid w-10 h-10 rounded-full bg-secondary place-items-center shrink-0">
              <Cookie size={18} strokeWidth={1.7}/>
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="cookie-title" className="font-display text-lg sm:text-xl tracking-tight">
                Rispettiamo la tua privacy
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                Usiamo cookie tecnici necessari al funzionamento. Con il tuo consenso possiamo usare anche cookie di
                analisi per migliorare il sito. Puoi cambiare idea in qualsiasi momento dalla nostra{' '}
                <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
              </p>

              {showDetails && (
                <div className="mt-4 space-y-3 rounded-xl border border-border/60 bg-secondary/40 p-4">
                  <label className="flex items-start gap-3 text-sm opacity-70 cursor-not-allowed">
                    <input type="checkbox" checked disabled className="mt-1"/>
                    <div>
                      <div className="font-medium">Necessari <span className="text-xs text-muted-foreground">(sempre attivi)</span></div>
                      <div className="text-xs text-muted-foreground">Sessione utente, autenticazione, sicurezza. Senza questi il sito non funziona.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <input
                      data-testid="cookie-analytics"
                      type="checkbox"
                      className="mt-1"
                      checked={prefs.analytics}
                      onChange={(e)=>setPrefs(p=>({...p, analytics: e.target.checked}))}
                    />
                    <div>
                      <div className="font-medium">Analitici</div>
                      <div className="text-xs text-muted-foreground">Statistiche aggregate anonime per capire quali terreni sono più visti e migliorare il sito.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <input
                      data-testid="cookie-marketing"
                      type="checkbox"
                      className="mt-1"
                      checked={prefs.marketing}
                      onChange={(e)=>setPrefs(p=>({...p, marketing: e.target.checked}))}
                    />
                    <div>
                      <div className="font-medium">Marketing</div>
                      <div className="text-xs text-muted-foreground">Contenuti personalizzati e comunicazioni promozionali via terze parti. Attualmente non utilizzati.</div>
                    </div>
                  </label>
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {!showDetails ? (
                  <>
                    <Button
                      size="sm"
                      className="rounded-full"
                      data-testid="cookie-accept-all"
                      onClick={acceptAll}
                    >
                      Accetta tutti
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      data-testid="cookie-reject-all"
                      onClick={rejectAll}
                    >
                      Rifiuta
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full"
                      data-testid="cookie-customize"
                      onClick={()=>setShowDetails(true)}
                    >
                      Personalizza
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      className="rounded-full"
                      data-testid="cookie-save-prefs"
                      onClick={saveCustom}
                    >
                      Salva preferenze
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={acceptAll}
                    >
                      Accetta tutti
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full"
                      onClick={()=>setShowDetails(false)}
                    >
                      Indietro
                    </Button>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={rejectAll}
              data-testid="cookie-close"
              aria-label="Chiudi (rifiuta non necessari)"
              className="ml-1 p-1 rounded-full hover:bg-secondary text-muted-foreground"
            >
              <X size={16}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
